import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdminPayment } from "../../services/payments"
import {
  ArrowLeftIcon,
  CreditCardIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  TicketIcon,
  TagIcon
} from "@heroicons/react/24/outline"

const AdminPaymentDetails = () => {
  const { paymentId } = useParams()
  const [payment, setPayment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const fetchPayment = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await getAdminPayment(paymentId)
      if (res.status === 200) {
        setPayment(res.data)
      } else {
        setError(res.error || "Payment not found")
      }
    } catch (err) {
      setError("Failed to fetch payment")
    }
    setLoading(false)
  }

  useEffect(() => { fetchPayment() }, [paymentId])

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        <p className="mt-2 text-slate-500">Loading payment...</p>
      </div>
    )
  }

  if (error || !payment) {
    return (
      <div className="max-w-3xl mx-auto">
        <Link to="/admin/payments" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Payments
        </Link>
        <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
          {error || "Payment not found"}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/admin/payments" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Payments
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-8 border-b border-slate-100">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-cyan-100 flex items-center justify-center">
                <CreditCardIcon className="w-8 h-8 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Payment Details</h1>
                <p className="text-slate-500 mt-1">Transaction ID: {payment.transactionId || payment._id}</p>
              </div>
            </div>
            <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold uppercase ${
              payment.status === 'PAID' || payment.status === 'SUCCESS' || payment.status === 'SUCCESSFUL' ? 'bg-green-100 text-green-700' :
              payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {payment.status}
            </span>
          </div>
        </div>

        {/* Payment Info Grid */}
        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Attendee Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <UserIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Name</p>
                  <p className="font-semibold text-slate-900">{payment.attendeeId?.name || 'Unknown'}</p>
                </div>
              </div>
              {payment.attendeeId?.email && (
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-slate-900">{payment.attendeeId.email}</p>
                </div>
              )}
              {payment.attendeeId?.phoneNumber && (
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="text-slate-900">{payment.attendeeId.phoneNumber}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Center Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <BuildingOfficeIcon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Organization</p>
                  <p className="font-semibold text-slate-900">{payment.centerId?.organization || 'Unknown'}</p>
                </div>
              </div>
              {payment.centerId?.email && (
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-slate-900">{payment.centerId.email}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="p-8 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Transaction Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500">Amount</p>
              <p className="text-2xl font-bold text-slate-900">₦{payment.amount?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Reference</p>
              <p className="font-mono text-slate-900">{payment.txRef || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Method</p>
              <p className="font-semibold text-slate-900">{payment.method || 'CARD'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Currency</p>
              <p className="font-semibold text-slate-900">{payment.currency || 'NGN'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Order ID</p>
              <p className="font-mono text-slate-600">{payment.orderId?._id || payment.orderId || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Payment Date</p>
              <p className="text-slate-900">{new Date(payment.createdAt).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Event/Offer Info */}
        {(payment.eventId || payment.offerId) && (
          <div className="p-8 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Related Item</h3>
            <div className="flex items-center gap-3">
              {payment.eventId ? (
                <>
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TicketIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Event</p>
                    <p className="font-semibold text-slate-900">{payment.eventId?.title || 'Event'}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <TagIcon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Offer</p>
                    <p className="font-semibold text-slate-900">{payment.offerId?.title || 'Offer'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPaymentDetails
